import type { Icon } from "@phosphor-icons/react";
import { withSource } from "~/actions/context";
import {
  type Action,
  type ActionContext,
  actionChildren,
  isVisible,
  resolve,
} from "~/actions/types";

export interface CommandEntry {
  id: string;
  name: string;
  section: string;
  icon?: Icon;
  keywords?: string;
  shortcut?: string[];
  /** True when selecting this row opens a nested page instead of performing something. */
  hasChildren: boolean;
  perform?: () => void;
}

export interface CommandGroup {
  section: string;
  entries: CommandEntry[];
}

function isSeparator(action: Action): boolean {
  return action.id === "separator" || action.id.startsWith("separator.");
}

/**
 * One level of the command bar. Actions with children are not flattened — they become rows
 * that push a page, which is what keeps "Appearance → Dark" discoverable instead of leaking
 * three theme rows into the top level.
 */
export function actionsToCommandEntries(
  actions: readonly Action[],
  context: ActionContext,
): CommandEntry[] {
  const ctx = withSource(context, "commandbar");
  const entries: CommandEntry[] = [];

  for (const action of actions) {
    if (isSeparator(action) || !isVisible(action, ctx)) {
      continue;
    }

    const children = actionChildren(action, ctx);
    const hasChildren = children.some((child) => isVisible(child, ctx));

    if (!hasChildren && !action.perform) {
      continue;
    }

    entries.push({
      id: action.id,
      name: resolve(action.name, ctx),
      section: resolve(action.section, ctx),
      ...(action.icon ? { icon: action.icon } : {}),
      ...(action.keywords ? { keywords: action.keywords } : {}),
      ...(action.shortcut ? { shortcut: action.shortcut } : {}),
      hasChildren,
      ...(action.perform && !hasChildren
        ? {
            perform: () => {
              void action.perform?.(ctx);
            },
          }
        : {}),
    });
  }

  return entries;
}

/** Stable grouping: sections appear in the order their first entry does. */
export function groupCommandEntries(entries: CommandEntry[]): CommandGroup[] {
  const groups: CommandGroup[] = [];
  for (const entry of entries) {
    const existing = groups.find((group) => group.section === entry.section);
    if (existing) {
      existing.entries.push(entry);
    } else {
      groups.push({ section: entry.section, entries: [entry] });
    }
  }
  return groups;
}

/**
 * Walks a page path (["theme"]) down the action tree and returns the actions at that level.
 * An unknown id resolves to an empty level rather than throwing — a stale persisted path
 * must not be able to break the command bar.
 */
export function resolveActionPath(
  actions: readonly Action[],
  path: readonly string[],
  context: ActionContext,
): Action[] {
  let level: Action[] = [...actions];
  for (const id of path) {
    const parent = level.find((action) => action.id === id);
    if (!parent) {
      return [];
    }
    level = actionChildren(parent, context);
  }
  return level;
}

export function pageTitle(
  actions: readonly Action[],
  path: readonly string[],
  context: ActionContext,
): string | undefined {
  if (path.length === 0) {
    return undefined;
  }
  let level: Action[] = [...actions];
  let title: string | undefined;
  for (const id of path) {
    const parent = level.find((action) => action.id === id);
    if (!parent) {
      return title;
    }
    title = resolve(parent.name, context);
    level = actionChildren(parent, context);
  }
  return title;
}
